import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import ls from "localstorage-slim";
import Select from "react-select";
import axios from "axios";
import { loadCss, getLowerCaseString, forceRound } from "helpers/function";
import { verifyIfAnyPermission, verifyPermission } from "helpers/permission";
import { verifyTokenExpire } from "middleware/verifyToken";
import { ERROR_401 } from "config/errorPage";
import appConfig from "config/appConfig";
import { DeleteConfirmation } from "views/components/ConfirmationAlert";
import { ToastBottomEnd } from "views/components/Toast";
import { confirmDeleteConfig } from "config/confirmConfig";
import {
  toastDeleteErrorMessageConfig,
  toastDeleteSuccessMessageConfig,
} from "config/toastConfig";
import { NUMBER_ELEMENT_PER_PAGE } from "constants/dataTable";
import LoadingTable from "views/components/LoadingTable";
import EmptyTable from "views/components/EmptyTable";
import HeaderTablePage from "views/components/HeaderTablePage";
import InfirmationTable from "views/components/InfirmationTable";
import Pagination from "views/components/Pagination";
import ExportButton from "views/components/ExportButton";
import StaffTableRow from "components/staff/StaffTableRow";

loadCss("/assets/plugins/custom/datatables/datatables.bundle.css");

const endPointConfig = {
  PRO: {
    plan: "PRO",
    list: `${appConfig.apiDomaine}/my/staff`,
    create: `${appConfig.apiDomaine}/my/staff/create`,
    destroy: (unitId) => `${appConfig.apiDomaine}/my/staff/${unitId}`,
  },
  MACRO: {
    holding: {
      create: `${appConfig.apiDomaine}/any/staff/create`,
      list: `${appConfig.apiDomaine}/any/staff`,
      destroy: (unitId) => `${appConfig.apiDomaine}/any/staff/${unitId}`,
    },
    filial: {
      create: `${appConfig.apiDomaine}/my/staff/create`,
      list: `${appConfig.apiDomaine}/my/staff`,
      destroy: (unitId) => `${appConfig.apiDomaine}/my/staff/${unitId}`,
    },
  },
  HUB: {
    plan: "HUB",
    list: `${appConfig.apiDomaine}/maybe/no/staff`,
    destroy: (unitId) => `${appConfig.apiDomaine}/maybe/no/staff/${unitId}`,
  },
};

const Staff = ({ userPermissions, plan, isHolding, userData }) => {
  const { t, ready } = useTranslation();
  ls.config.encrypt = true;

  const hasPermissionToView = useMemo(() => {
    return (
      verifyPermission(userPermissions, "list-staff-from-any-unit") ||
      verifyPermission(userPermissions, "list-staff-from-my-unit") ||
      verifyPermission(userPermissions, "list-staff-from-maybe-no-unit")
    );
  }, [userPermissions]);

  useEffect(() => {
    if (!hasPermissionToView) {
      window.location.href = ERROR_401;
    }
  }, [hasPermissionToView]);

  const endPoint = useMemo(() => {
    if (plan === "MACRO") {
      if (verifyPermission(userPermissions, "list-any-unit"))
        return endPointConfig[plan].holding;
      else if (verifyPermission(userPermissions, "list-my-unit"))
        return endPointConfig[plan].filial;
    } else {
      return endPointConfig[plan];
    }
  }, [plan, userPermissions]);

  const unitTriable = useMemo(
    () =>
      plan === "PRO" ||
      verifyPermission(userPermissions, "list-staff-from-my-unit")
        ? "unit_id"
        : "institution_id",
    [plan, userPermissions]
  );

  const userRoles = useMemo(() => {
    try {
      const userData = JSON.parse(ls.get("userData"));
      return userData?.data?.roles?.map((role) => role.name) || [];
    } catch (error) {
      console.error("Error parsing user data:", error);
      return [];
    }
  }, []);

  const [loading, setLoading] = useState(true);
  const [staffs, setStaffs] = useState([]);
  const [entities, setEntities] = useState(null);
  const [entitiesOption, setEntitiesOption] = useState([]);
  const [numberPerPage, setNumberPerPage] = useState(NUMBER_ELEMENT_PER_PAGE);
  const [activeNumberPage, setActiveNumberPage] = useState(1);
  const [numberPage, setNumberPage] = useState(0);
  const [showList, setShowList] = useState([]);
  const [total, setTotal] = useState(0);
  const [nextUrl, setNextUrl] = useState(null);
  const [prevUrl, setPrevUrl] = useState(null);

  const fetchEntities = async () => {
    if (!endPoint || !verifyTokenExpire()) return;

    try {
      const response = await axios.get(endPoint.create);
      if (response.data) {
        if (response.data.institutions) {
          setEntitiesOption(response.data.institutions);
        } else if (response.data.units) {
          setEntitiesOption(response.data.units);
        }
      }
    } catch (error) {
      console.error("Error fetching entities:", error);
    }
  };

  const fetchStaffData = async () => {
    if (!endPoint || !verifyTokenExpire()) return;
    setLoading(true);
    try {
      // const url = `${endPoint.list}?size=${numberPerPage}${
      //   entities !== null ? `&${unitTriable}=${entities.value}` : !userRoles.includes("admin-filial") ? `&${unitTriable}=${userData.institution.id}` : ''
      // }`;

      const url = `${endPoint.list}?size=${numberPerPage}${entities !== null ? `&${unitTriable}=${entities.value}` : ''}`;

      const response = await axios.get(url);
      const data = response.data;

      setNumberPage(forceRound(data.total / numberPerPage));
      setActiveNumberPage(1)
      setShowList(data.data.slice(0, numberPerPage));
      setStaffs(data.data);
      setTotal(data.total);
      setPrevUrl(data.prev_page_url);
      setNextUrl(data.next_page_url);
    } catch (error) {
      console.error("Error fetching staff data:", error);
    } finally {
      setLoading(false);
    }
  }; 
  
  useEffect(() => {
    fetchStaffData();
  }, [entities]); //[endPoint, entities, unitTriable, numberPerPage]

  useEffect(() => {
    if (hasPermissionToView && verifyTokenExpire()) {
      fetchEntities();
    }
  }, [endPoint]); //[hasPermissionToView, fetchStaffData, fetchEntities]


  // console.log(verifyIfAnyPermission(userPermissions, ["store-staff-from-any-unit", "store-staff-from-my-unit", "store-staff-from-maybe-no-unit"]));
  
  const handleSearch = useCallback(
    async (e) => {
      if (!verifyTokenExpire()) return;

      const searchValue = e.target.value;

      setLoading(true);
      try {
        const url = searchValue
          ? `${endPoint.list}?key=${getLowerCaseString(
              searchValue
            )}&size=${numberPerPage}${
              entities !== null ? `&${unitTriable}=${entities.value}` : ""
            }`
          : `${endPoint.list}?size=${numberPerPage}${
              entities !== null ? `&${unitTriable}=${entities.value}` : ""
            }`;

        const response = await axios.get(url);
        const data = response.data;

        setStaffs(data.data);
        setShowList(data.data.slice(0, numberPerPage));
        setTotal(data.total);
        setNumberPage(forceRound(data.total / numberPerPage));
        setPrevUrl(data.prev_page_url);
        setNextUrl(data.next_page_url);

        if (!searchValue) {
          setActiveNumberPage(1);
        }
      } catch (error) {
        console.error("Error searching staff:", error);
      } finally {
        setLoading(false);
      }
    },
    [endPoint, entities, unitTriable, numberPerPage]
  );

  const handleChangePerPage = useCallback(
    (e) => {
      if (!verifyTokenExpire()) return;

      const value = parseInt(e.target.value);
      setLoading(true);

      axios
        .get(
          `${endPoint.list}?size=${value}${
            entities !== null ? `&${unitTriable}=${entities.value}` : ""
          }`
        )
        .then((response) => {
          setActiveNumberPage(1);
          setStaffs(response.data.data);
          setShowList(response.data.data.slice(0, response.data.per_page));
          setTotal(response.data.total);
          setNumberPage(
            forceRound(response.data.total / response.data.per_page)
          );
          setPrevUrl(response.data.prev_page_url);
          setNextUrl(response.data.next_page_url);
          setNumberPerPage(value);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    },
    [endPoint, entities, unitTriable]
  );

  const handlePageClick = useCallback(
    (e, page) => {
      if (!verifyTokenExpire()) return;

      e.preventDefault();
      setActiveNumberPage(page);
      setLoading(true);

      axios
        .get(
          `${endPoint.list}?page=${page}&size=${numberPerPage}${
            entities !== null ? `&${unitTriable}=${entities.value}` : ""
          }`
        )
        .then((response) => {
          setPrevUrl(response.data.prev_page_url);
          setNextUrl(response.data.next_page_url);
          setStaffs(response.data.data);
          setShowList(response.data.data.slice(0, numberPerPage));
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    },
    [endPoint, entities, unitTriable, numberPerPage]
  );

  const handleNextPage = useCallback(
    (e) => {
      e.preventDefault();
      if (
        activeNumberPage <= numberPage &&
        nextUrl !== null &&
        verifyTokenExpire()
      ) {
        setActiveNumberPage(activeNumberPage + 1);
        setLoading(true);

        axios
          .get(
            `${nextUrl}?size=${numberPerPage}${
              entities !== null ? `&${unitTriable}=${entities.value}` : ""
            }`
          )
          .then((response) => {
            console.log("nextUrl", response);
            setPrevUrl(response.data.prev_page_url);
            setNextUrl(response.data.next_page_url);
            setStaffs(response.data.data);
            setShowList(response.data.data.slice(0, numberPerPage));
            setLoading(false);
          })
          .catch(() => {
            setLoading(false);
          });
      }
    },
    [
      activeNumberPage,
      numberPage,
      nextUrl,
      numberPerPage,
      entities,
      unitTriable,
    ]
  );

  const handlePreviousPage = useCallback(
    (e) => {
      e.preventDefault();
      if (activeNumberPage >= 1 && prevUrl !== null && verifyTokenExpire()) {
        setActiveNumberPage(activeNumberPage - 1);
        setLoading(true);

        axios
          .get(
            `${prevUrl}?size=${numberPerPage}${
              entities !== null ? `&${unitTriable}=${entities.value}` : ""
            }`
          )
          .then((response) => {
            setPrevUrl(response.data.prev_page_url);
            setNextUrl(response.data.next_page_url);
            setStaffs(response.data.data);
            setShowList(response.data.data.slice(0, numberPerPage));
            setLoading(false);
          })
          .catch(() => {
            setLoading(false);
          });
      }
    },
    [activeNumberPage, prevUrl, numberPerPage, entities, unitTriable]
  );

  const handleEntityChange = useCallback((selected) => {
    setEntities(selected);
  }, []);

  const handleDeleteStaff = useCallback(
    (staffId, index) => {
      if (!verifyTokenExpire()) return;

      DeleteConfirmation.fire(confirmDeleteConfig()).then((result) => {
        if (result.value) {
          axios
            .delete(endPoint.destroy(staffId))
            .then(() => {
              const newStaffs = [...staffs];
              newStaffs.splice(index, 1);
              setStaffs(newStaffs);

              setLoading(true);
              const pageToLoad =
                showList.length > 1
                  ? activeNumberPage
                  : Math.max(1, activeNumberPage - 1);

              const url = `${
                endPoint.list
              }?page=${pageToLoad}&size=${numberPerPage}${
                entities !== null ? `&${unitTriable}=${entities.value}` : ""
              }`;

              axios
                .get(url)
                .then((response) => {
                  setPrevUrl(response.data.prev_page_url);
                  setNextUrl(response.data.next_page_url);
                  setStaffs(response.data.data);
                  setShowList(response.data.data.slice(0, numberPerPage));
                  setTotal(response.data.total);
                  setNumberPage(
                    forceRound(response.data.total / numberPerPage)
                  );

                  if (showList.length <= 1) {
                    setActiveNumberPage(Math.max(1, activeNumberPage - 1));
                  }

                  setLoading(false);
                  ToastBottomEnd.fire(toastDeleteSuccessMessageConfig());
                })
                .catch(() => {
                  setLoading(false);
                });
            })
            .catch((error) => {
              const errorResponse = error.response?.data?.error;
              ToastBottomEnd.fire(
                errorResponse
                  ? toastDeleteErrorMessageConfig(errorResponse)
                  : toastDeleteErrorMessageConfig()
              );
            });
        }
      });
    },
    [
      endPoint,
      staffs,
      showList.length,
      activeNumberPage,
      numberPerPage,
      entities,
      unitTriable,
    ]
  );

  const formatUnitSelectOption = useCallback(
    (options, labelKey, translate, valueKey = "id") => {
        if (!options) return [];
        return options?.map((option) => ({
          value: option[valueKey],
          label: translate ? option[labelKey][translate] : option[labelKey].fr ? option[labelKey].fr : option[labelKey],
          lead: option.lead,
        }));
      },
    []
  );

  // useEffect(() => {
  //   if (userData.institution) {
  //     userRoles.includes("admin-filial") ?
  //     setEntities(formatUnitSelectOption([userData.data.identite.staff.unit], "name")[0]) :
  //     setEntities(formatUnitSelectOption([userData.institution], "name")[0]);
  //   }
  // }, []);

  if (!ready || !hasPermissionToView) return null;

  const showInstitution = verifyPermission(
    userPermissions,
    "list-staff-from-any-unit"
  );

  return (
    <div
      className="kt-content kt-grid__item kt-grid__item--fluid kt-grid kt-grid--hor"
      id="kt_content"
    >
      <div className="kt-subheader kt-grid__item" id="kt_subheader">
        <div className="kt-container kt-container--fluid">
          <div className="kt-subheader__main">
            <h3 className="kt-subheader__title">{t("Paramètres")}</h3>
            <span className="kt-subheader__separator kt-hidden" />
            <div className="kt-subheader__breadcrumbs">
              <button className="kt-subheader__breadcrumbs-home btn btn-link">
                <i className="flaticon2-shelter" />
              </button>
              <span className="kt-subheader__breadcrumbs-separator" />
              <button
                onClick={(e) => e.preventDefault()}
                className="kt-subheader__breadcrumbs-link btn btn-link"
                style={{ cursor: "text" }}
              >
                {t("Agent")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="kt-container kt-container--fluid kt-grid__item kt-grid__item--fluid">
        <InfirmationTable
          information={
            <>
              <span className="kt-badge kt-badge--success kt-badge--inline">
                L
              </span>{" "}
              : {t("Responsable d'unité")}
            </>
          }
        />

        <div className="kt-portlet">
          <HeaderTablePage
            addPermission={[
              "store-staff-from-any-unit",
              "store-staff-from-my-unit",
              "list-staff-from-maybe-no-unit",
            ]}
            title={t("Agent")}
            addText={verifyIfAnyPermission(userPermissions, ["store-staff-from-any-unit", "store-staff-from-my-unit", "store-staff-from-maybe-no-unit"]) && t("Ajouter")}
            addLink={verifyIfAnyPermission(userPermissions, ["store-staff-from-any-unit", "store-staff-from-my-unit", "store-staff-from-maybe-no-unit"]) && "/settings/staffs/add"}
          />

          <div className="kt-portlet__body">
            <div
              id="kt_table_1_wrapper"
              className="dataTables_wrapper dt-bootstrap4"
            >
              <div className="row mb-3">
                <ExportButton
                  className="text-right"
                  style={{ marginLeft: "61%" }}
                  downloadLink={verifyIfAnyPermission(userPermissions, ["store-staff-from-any-unit", "store-staff-from-my-unit", "store-staff-from-maybe-no-unit"]) && `${appConfig.apiDomaine}/download-excel/staffs`}
                  pageUrl={verifyIfAnyPermission(userPermissions, ["store-staff-from-any-unit", "store-staff-from-my-unit", "store-staff-from-maybe-no-unit"]) && "/settings/staffs/import"}
                />
              </div>

              <div className="row">
                <div className="col-sm-6 text-left">
                  <div id="kt_table_1_filter" className="dataTables_filter">
                    <label>
                      {t("Recherche")}:
                      <input
                        id="myInput"
                        type="text"
                        onKeyUp={handleSearch}
                        className="form-control form-control-sm"
                        placeholder={t("Nom, Téléphone, Email")}
                        aria-controls="kt_table_1"
                      />
                    </label>
                  </div>
                </div>
                <div className="col-sm-6">
                  <div className="row col">
                    <label
                      htmlFor="unit"
                      className="col-xl-2 col-lg-2 col-form-label"
                    >
                      {plan === "PRO"
                        ? t("Unité")
                        : t(
                            !isHolding
                              ? "Unité"
                              : "Institution"
                          )}
                    </label>
                    <div className="" style={{ width: "83%" }}>
                      <Select
                        isClearable
                        value={entities}
                        onChange={handleEntityChange}
                        placeholder={`${t("Filtrer en fonction de l'")} ${t(
                          !isHolding
                            ? "Unité"
                            : "Institution"
                        )}`}
                        options={formatUnitSelectOption(
                          entitiesOption,
                          "name"
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {loading ? (
                <LoadingTable />
              ) : (
                <>
                  <div className="row">
                    <div className="col-sm-12" style={{ overflowX: "auto" }}>
                      <table
                        className="table table-striped table-bordered table-hover table-checkable dataTable dtr-inline"
                        id="myTable"
                        role="grid"
                        aria-describedby="kt_table_1_info"
                        style={{ width: "952px" }}
                      >
                        <thead>
                          <tr role="row">
                            <th className="sorting">{t("Nom")}</th>
                            <th className="sorting">{t("Téléphone")}</th>
                            <th className="sorting">Email</th>
                            <th className="sorting">{t("Unité")}</th>
                            {showInstitution && (
                              <th className="sorting">{t("Institution")}</th>
                            )}
                            <th className="sorting">{t("Fonction")}</th>
                            <th className="sorting">{t("Actions")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {staffs.length ? (
                            showList && showList.length ? (
                              showList.map((staff, index) => (
                                <StaffTableRow
                                  key={staff.id || index}
                                  staff={staff}
                                  index={index}
                                  onDelete={handleDeleteStaff}
                                  t={t}
                                  userPermissions={userPermissions}
                                />
                              ))
                            ) : (
                              <EmptyTable search={true} />
                            )
                          ) : (
                            <EmptyTable />
                          )}
                        </tbody>
                        <tfoot>
                          <tr>
                            <th>{t("Nom")}</th>
                            <th>{t("Téléphone")}</th>
                            <th>{t("Email")}</th>
                            <th>{t("Unité")}</th>
                            {showInstitution && <th>{t("Institution")}</th>}
                            <th>{t("Position")}</th>
                            <th>{t("Actions")}</th>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>

                  {/* Pagination */}
                  <div className="row">
                    <div className="col-sm-12 col-md-5">
                      <div
                        className="dataTables_info"
                        id="kt_table_1_info"
                        role="status"
                        aria-live="polite"
                      >
                        {t("Affichage de")} {showList.length}{" "}
                        {t("sur")} {total} {t("données")}
                      </div>
                    </div>
                    {showList && showList.length > 0 && (
                      <div className="col-sm-12 col-md-7 dataTables_pager">
                        <Pagination
                          numberPerPage={numberPerPage}
                          onChangeNumberPerPage={handleChangePerPage}
                          activeNumberPage={activeNumberPage}
                          onClickPage={handlePageClick}
                          onClickPreviousPage={handlePreviousPage}
                          onClickNextPage={handleNextPage}
                          numberPage={numberPage}
                        />
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  userData: state.user.user,
  userPermissions: state.user.user.permissions,
  isHolding: state?.user?.user?.institution?.institution_type?.name === "holding",
  plan: state.plan.plan,
});

export default connect(mapStateToProps)(Staff);
